import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useOnboardingDashboardConfig } from '@frontend/onboarding-dashboard';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, NakedButton, Text } from '@frontend/design-system';
import { CommonActionTrackingIds, getPaymentHVATrackingIds } from '../../tracking-ids';
import { PaymentsProviderWrapper } from '../common/PaymentsProviderWrapper';
import { ActionCards } from './ActionCards';
import { AppointmentsPanel } from './AppointmentsPanel';
import { DismissButton } from './DismissButton';
import { PaymentContentSlider } from './PaymentContentSlider';

interface PaymentsPromotionDashboardCardProps {
  styles: Interpolation<Theme>;
  onClose?: () => void;
  innerRef?: React.LegacyRef<HTMLDivElement>;
}

const PROMOTION_SOURCE = 'dashboard';
const FEATURE_NOTIFICATION_SOURCE = PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_HVA_BANNER;
const NOTIFICATION_FEATURE = PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_WEAVE_PAY;

const DISMISS_ACTIONS = [
  PlatformLocationFeatureV1.Types.Action.ACTION_REMIND_AFTER_3_DAYS,
  PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN,
];
const HOUR_IN_MS = 1000 * 60 * 60;

const PaymentsPromotionBanner: FC<PaymentsPromotionDashboardCardProps> = ({ styles, innerRef, onClose }) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'payments' });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const { showOnboardingDashboard } = useOnboardingDashboardConfig();
  const isHiddenByOnboardingDashboardRef = useRef(false);

  const { mutate, isLoading: isActionInProgress } =
    PlatformLocationFeatureV1.Mutations.useCreateOrUpdateFeatureNotificationUserActionMutation({
      options: {
        onSuccess: (_, request) => {
          if (!!request.actionEnum && DISMISS_ACTIONS.includes(request.actionEnum)) {
            onClose?.();
          }
        },
      },
    });

  const {
    data,
    isFetched,
    isFetching: isFetchingActions,
  } = PlatformLocationFeatureV1.Queries.useCheckFeatureNotificationStatusQuery({
    request: {
      notificationFeature: NOTIFICATION_FEATURE,
      sourceEnum: FEATURE_NOTIFICATION_SOURCE,
    },
    options: {
      retry: false,
      cacheTime: HOUR_IN_MS,
      staleTime: HOUR_IN_MS,
    },
  });

  const { currentStatus, dismissActions } = useMemo(() => {
    return {
      currentStatus: data?.currentStatus,
      dismissActions: data?.dismissAction?.filter((action) => DISMISS_ACTIONS.includes(action)) || [],
    };
  }, [data]);

  const handleAction = (action: PlatformLocationFeatureV1.Types.Action) => {
    mutate({
      actionEnum: action,
      sourceEnum: [FEATURE_NOTIFICATION_SOURCE],
      notificationFeature: NOTIFICATION_FEATURE,
    });

    // Optimistically update the UI
    switch (action) {
      case PlatformLocationFeatureV1.Types.Action.ACTION_REMIND_AFTER_3_DAYS:
      case PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN:
        setIsDismissed(true);
        break;
      case PlatformLocationFeatureV1.Types.Action.ACTION_COLLAPSE:
        setIsCollapsed(true);
        break;
      case PlatformLocationFeatureV1.Types.Action.ACTION_EXPAND:
        setIsCollapsed(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (currentStatus) {
      setIsCollapsed(currentStatus === PlatformLocationFeatureV1.Types.Action.ACTION_COLLAPSE);
      setIsDismissed(DISMISS_ACTIONS.includes(currentStatus));
    }
  }, [currentStatus]);

  // TODO: Implement logic for appointments when api is ready
  const appointments: Appointment[] = [];

  const disableActions = isFetchingActions || isActionInProgress;

  if (showOnboardingDashboard || isHiddenByOnboardingDashboardRef.current) {
    isHiddenByOnboardingDashboardRef.current = true;
    return null; // Do not show the card if onboarding dashboard is enabled. Onboarding widget takes too much space on dashboard so we hide this card.
  }

  // Hide the card if it is dismissed or if the current status is one of the dismiss actions
  if (isDismissed || (currentStatus && DISMISS_ACTIONS.includes(currentStatus)) || !isFetched) {
    return null;
  }

  return (
    <div css={[containerStyles, styles]} ref={innerRef}>
      <div css={titleStyles}>
        <div css={textIconStyles}>
          <Icon name='bulb-small' css={mainIconStyles} />
          <Text size='large' weight='bold'>
            {t('Your plan includes Payments')}
          </Text>
        </div>
        <div css={accordionActionStyles}>
          {isCollapsed ? (
            <>
              <DismissButton
                source={PROMOTION_SOURCE}
                dismissActions={dismissActions}
                onDismiss={(action) => handleAction(action)}
                disabled={disableActions}
              />
              <Button
                variant='tertiary'
                trackingId={getPaymentHVATrackingIds({
                  source: PROMOTION_SOURCE,
                  trackingId: CommonActionTrackingIds.EXPAND_CARD,
                })}
                disabled={disableActions}
                onClick={() => handleAction(PlatformLocationFeatureV1.Types.Action.ACTION_EXPAND)}
              >
                {t('Learn More')}
              </Button>
            </>
          ) : (
            <NakedButton
              css={[collapseButtonStyles, { gap: theme.spacing(0.5), opacity: disableActions ? 0.5 : 1 }]}
              trackingId={getPaymentHVATrackingIds({
                source: PROMOTION_SOURCE,
                trackingId: CommonActionTrackingIds.COLLAPSE_CARD,
              })}
              disabled={disableActions}
              onClick={() => handleAction(PlatformLocationFeatureV1.Types.Action.ACTION_COLLAPSE)}
            >
              <Icon name={'collapse-small'} css={{ color: theme.colors.neutral50 }} />
              <Text size='medium' weight='bold' css={collapseActionLabelStyles}>
                {t('Collapse')}
              </Text>
            </NakedButton>
          )}
        </div>
      </div>
      <div css={[bodyStyles, { height: isCollapsed ? 0 : 'auto' }]}>
        <PaymentContentSlider styles={contentSectionStyles} />
        {!!appointments?.length ? (
          <AppointmentsPanel appointments={appointments} styles={actionSectionStyles} />
        ) : (
          <ActionCards styles={actionSectionStyles} />
        )}
      </div>
    </div>
  );
};

export const PaymentsPromotionDashboardCard: FC<PaymentsPromotionDashboardCardProps> = (props) => {
  const { selectedLocationIds } = useAppScopeStore();
  const selectedLocationId = selectedLocationIds[0];

  return (
    <PaymentsProviderWrapper locationId={selectedLocationId} source={PROMOTION_SOURCE}>
      <PaymentsPromotionBanner {...props} />
    </PaymentsProviderWrapper>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.borderRadius.large,
  backgroundColor: theme.colors.white,
});

const titleStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  gap: theme.spacing(1),
});

const textIconStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
});

const collapseButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  height: 32,
  flexShrink: 0,
});

const bodyStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  overflow: 'hidden',
});

const contentSectionStyles = css({
  flex: 1.5,
  minWidth: 400,
  [`@media (max-width: ${breakpoints.medium.max}px)`]: {
    flex: 1,
    minWidth: 300,
  },
});

const actionSectionStyles = css({
  flex: 1,
  minWidth: 300,
});

const accordionActionStyles = css({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  justifyContent: 'flex-end',
});

const collapseActionLabelStyles = css({
  color: theme.colors.neutral50,
  [`@media (max-width: ${breakpoints.small.max}px)`]: {
    display: 'none',
  },
});

const mainIconStyles = css({
  borderRadius: theme.borderRadius.full,
  backgroundColor: theme.colors.tangerine10,
  color: theme.colors.tangerine50,
  padding: theme.spacing(0.5),
  boxSizing: 'content-box',
});
